import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { labels } from './labels';

const EDGE = 8; // px kept between the bubble and the viewport edge
const ARROW_W = 12; // QA-134: the Vulcan Tooltip arrow, 12 by 6 px
const ARROW_H = 6;
const HOVER_MS = 100; // a mouse hover waits this long; focus and click open at once
const GRACE_MS = 100; // the pointer may cross the gap between trigger and bubble
// The `@types/react` version here has no `popover` attribute; a spread skips the prop check.
const popoverManual = { popover: 'manual' };

/**
 * QA-133: the tip behavior on its own, for a button that is not the ⓘ (the trash icon of a Test
 * runs row). `pin: true` is the ⓘ: a click pins the bubble. `pin: false` is an action button: hover
 * and focus open the bubble, and the caller's own `onClick` runs at once, so a tap never needs a
 * second tap. Spread `buttonProps` on the button, and render `bubble` beside it, never inside it.
 *
 * QA-134: the form is the Vulcan Tooltip. A mouse hover opens it after 100 ms (QA-135); focus and click open
 * it at once. It stays open while the pointer is on the button or the bubble (WCAG 1.4.13), with a
 * 100 ms grace across the gap. It opens above the button, and below when there is no room.
 */
export function useTip(text: string, pin: boolean) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // A click pins the tip open; hover and focus open it without a pin.
  const pinned = useRef(false);
  // Whether a pin held when the pointer went down. Hover and focus open the tip before the click
  // event, so the click reads this instead of `open`. `null` means a keyboard click.
  const openAtPointerDown = useRef<boolean | null>(null);

  // QA-135: Escape pressed during the hover wait cancels the open. The listener lives only while
  // a timer runs; `clear` drops both.
  const dropEscape = useRef<() => void>();
  const clear = () => {
    clearTimeout(timer.current);
    dropEscape.current?.();
    dropEscape.current = undefined;
  };
  const set = (next: boolean) => {
    clear();
    setOpen(next);
  };
  const later = (next: boolean, ms: number) => {
    clear();
    timer.current = setTimeout(() => {
      clear();
      setOpen(next);
    }, ms);
    if (next) {
      const onKey = (e: KeyboardEvent) => e.key === 'Escape' && clear();
      document.addEventListener('keydown', onKey);
      dropEscape.current = () => document.removeEventListener('keydown', onKey);
    }
  };
  // A focused button keeps its bubble until blur, and a pinned bubble until a second click.
  const leave = (e: { pointerType: string }) => {
    if (e.pointerType === 'mouse' && document.activeElement !== button.current) later(false, GRACE_MS);
  };
  const enter = (e: { pointerType: string }) => {
    if (e.pointerType !== 'mouse') return;
    clear();
    if (!open) later(true, HOVER_MS);
  };
  useEffect(() => clear, []);

  const place = () => {
    const el = bubble.current;
    const anchor = button.current;
    if (!el || !anchor) return;
    const at = anchor.getBoundingClientRect();
    const center = at.left + at.width / 2;
    const left = Math.max(EDGE, Math.min(center - el.offsetWidth / 2, window.innerWidth - el.offsetWidth - EDGE));
    // Top center by default; Bottom center when there is no room above. A clamp at the inline edge
    // gives the Vulcan Top left or Top right: the arrow keeps pointing at the button.
    const above = at.top - el.offsetHeight - ARROW_H >= EDGE;
    el.dataset.placement = above ? 'top' : 'bottom';
    el.style.insetInlineStart = `${left}px`;
    el.style.insetBlockStart = `${above ? at.top - el.offsetHeight - ARROW_H : at.bottom + ARROW_H}px`;
    el.style.setProperty('--tip-arrow-x', `${Math.max(ARROW_W, Math.min(center - left, el.offsetWidth - ARROW_W))}px`);
  };

  useLayoutEffect(() => {
    const el = bubble.current;
    if (!el) return;
    if (!open) {
      pinned.current = false;
      el.hidePopover();
      return;
    }
    el.showPopover();
    place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && set(false);
    document.addEventListener('keydown', onKey);
    // The bubble is fixed to the viewport, so it follows its button when the page scrolls or resizes.
    // A focus that scrolls the button into view must not close it.
    window.addEventListener('scroll', place, { capture: true, passive: true });
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, { capture: true });
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const buttonProps = {
    ref: button as Ref<HTMLButtonElement>,
    'aria-describedby': id,
    onPointerEnter: enter,
    onPointerLeave: leave,
    onFocus: () => set(true),
    onBlur: () => set(false),
    ...(pin && {
      onPointerDown: () => {
        openAtPointerDown.current = open && pinned.current;
      },
      onClick: () => {
        const was = openAtPointerDown.current ?? open;
        openAtPointerDown.current = null;
        pinned.current = !was;
        set(!was);
      },
    }),
  };
  const bubbleEl: ReactNode = (
    <span
      ref={bubble}
      id={id}
      role="tooltip"
      {...popoverManual}
      className="info-tip__bubble"
      onPointerEnter={enter}
      onPointerLeave={leave}
    >
      {text}
    </span>
  );
  return { buttonProps, bubble: bubbleEl, hide: () => set(false) };
}

/** QA-134: the Vulcan Design Library "Info (outline)" icon (node 9965:19900), drawn inline. */
function InfoIcon() {
  return (
    <svg className="info-tip__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path
        fillRule="evenodd"
        d="M11 7C11 7.55228 10.5523 8 10 8C9.44772 8 9 7.55228 9 7C9 6.44772 9.44772 6 10 6C10.5523 6 11 6.44772 11 7ZM10.75 10C10.75 9.58579 10.4142 9.25 10 9.25C9.58579 9.25 9.25 9.58579 9.25 10V14C9.25 14.4142 9.58579 14.75 10 14.75C10.4142 14.75 10.75 14.4142 10.75 14V10ZM18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM16.5 10C16.5 13.5899 13.5899 16.5 10 16.5C6.41015 16.5 3.5 13.5899 3.5 10C3.5 6.41015 6.41015 3.5 10 3.5C13.5899 3.5 16.5 6.41015 16.5 10Z"
      />
    </svg>
  );
}

/**
 * QA-126: a small info button (the Vulcan "Info (outline)" icon) that shows a short text on pointer hover, on keyboard focus, and on
 * click or tap. It closes on Escape, on blur, and on a second click.
 *
 * Placement rules:
 * - Put the ⓘ as a sibling of the label text. Never inside a `<label>` or inside another button
 *   (the sort button of `SortHead` in `Documents.tsx`, for example).
 * - It sits in a table head cell without clipping: the bubble is a `popover="manual"` element, so
 *   it renders in the top layer, outside the `overflow-x: auto` of `.responsive-table`.
 * - The caller passes `label` (the name of what the text is about) and `text` (a `labels.ts` value).
 *   The bubble stays in the DOM while closed, so `aria-describedby` always resolves.
 */
export function InfoTip({ label, text }: { label: string; text: string }) {
  const tip = useTip(text, true);
  return (
    <span className="info-tip">
      <button
        type="button"
        className="info-tip__button"
        aria-label={labels.infoTip.about(label)}
        {...tip.buttonProps}
      >
        <InfoIcon />
      </button>
      {tip.bubble}
    </span>
  );
}
